//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@carbon/icons-react';
import { ChevronDownIcon } from 'lucide-react';
import {
  TeamspaceIdentifier,
  CreateAnnouncementRequest,
  AnnouncementScope,
} from '../Sidebar/SidebarTypes';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/** Return the current local time as an HH:mm string. */
const currentTimeHHMM = (): string => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

interface CreateAnnouncementPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (teamspaceId: string, request: CreateAnnouncementRequest) => void;
  teamspaces: TeamspaceIdentifier[];
  currentTeamspaceId: string | undefined;
}

const CreateAnnouncementPanel: React.FC<CreateAnnouncementPanelProps> = ({
  isOpen,
  onClose,
  onSubmit,
  teamspaces,
  currentTeamspaceId,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<'general' | 'maintenance' | 'update'>(
    'general',
  );
  const [startDate, setStartDate] = useState<Date | undefined>(
    () => new Date(),
  );
  const [startTime, setStartTime] = useState(() => currentTimeHHMM());
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [endTime, setEndTime] = useState('');
  const [openStartDate, setOpenStartDate] = useState(false);
  const [openEndDate, setOpenEndDate] = useState(false);

  const formatDateForDisplay = useCallback((date: Date | undefined): string => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }, []);

  const formatDateForISO = useCallback((date: Date | undefined): string => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);
  const [isGlobal, setIsGlobal] = useState(true);
  const [selectedTeamspaceId, setSelectedTeamspaceId] = useState(
    currentTeamspaceId ?? '',
  );

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) =>
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

  const filteredTeamspaces = teamspaces.filter(
    (ts) => ts.name !== 'Initial TeamSpace',
  );

  const startDateStr = formatDateForISO(startDate);
  const endDateStr = formatDateForISO(endDate);

  const TITLE_MAX_LENGTH = 20;
  const CONTENT_MAX_LENGTH = 1000;

  const endBeforeStart =
    startDateStr && endDateStr && endDateStr < startDateStr;
  const sameDay = startDateStr && endDateStr && startDateStr === endDateStr;
  // Flag a time conflict when both times are filled and either:
  //  - both dates are on the same day, OR
  //  - end date hasn't been picked yet (so same-day is the implicit assumption)
  const endTimeBeforeStart =
    startTime &&
    endTime &&
    endTime <= startTime &&
    (sameDay || (startDate && !endDate));
  const hasDateTimeConflict = !!(endBeforeStart || endTimeBeforeStart);

  const missingFields: string[] = [];
  if (!title.trim()) missingFields.push(t('announcement-title'));
  if (!content.trim()) missingFields.push(t('announcement-content'));
  if (!startDate) missingFields.push(t('announcement-start-date'));
  if (!startTime) missingFields.push(t('announcement-start-time'));
  if (!endDate) missingFields.push(t('announcement-end-date'));
  if (!endTime) missingFields.push(t('announcement-end-time'));
  if (!isGlobal && !selectedTeamspaceId)
    missingFields.push(t('announcement-teamspace-select'));
  const isContentOverLimit = content.length > CONTENT_MAX_LENGTH;
  const isTitleOverLimit = title.length > TITLE_MAX_LENGTH;
  const isFormValid =
    missingFields.length === 0 &&
    !isContentOverLimit &&
    !isTitleOverLimit &&
    !hasDateTimeConflict;

  const tooltipParts: string[] = [];
  if (missingFields.length > 0)
    tooltipParts.push(
      `${t('announcement-fields-required')}: ${missingFields.join(', ')}`,
    );
  if (hasDateTimeConflict)
    tooltipParts.push(
      endBeforeStart
        ? t('announcement-end-before-start')
        : t('announcement-end-time-before-start'),
    );
  const disabledTooltip = tooltipParts.join('. ');

  const handleSubmit = () => {
    if (!isFormValid) return;

    const scope: AnnouncementScope = isGlobal ? 'GLOBAL' : 'TEAMSPACE';
    const teamspaceId = isGlobal
      ? currentTeamspaceId ?? ''
      : selectedTeamspaceId;

    const startDateTime = new Date(
      `${startDateStr}T${startTime}`,
    ).toISOString();

    const endDateTime = new Date(`${endDateStr}T${endTime}`).toISOString();

    const request: CreateAnnouncementRequest = {
      title: title.trim(),
      message: content.trim(),
      type,
      scope,
      startsAt: startDateTime,
      expiresAt: endDateTime,
    };

    onSubmit(teamspaceId, request);
    resetForm();
  };

  const fieldWarnings: Record<string, string> = {};
  if (touched.title && !title.trim())
    fieldWarnings.title = t('announcement-title-required');
  else if (isTitleOverLimit)
    fieldWarnings.title = t('announcement-title-max-length', {
      max: TITLE_MAX_LENGTH,
    });
  if (touched.content && !content.trim())
    fieldWarnings.content = t('announcement-content-required');
  else if (isContentOverLimit)
    fieldWarnings.content = t('announcement-content-max-length', {
      max: CONTENT_MAX_LENGTH,
    });
  if (touched.startDate && !startDate)
    fieldWarnings.startDate = t('announcement-start-date-required');
  if (touched.startTime && !startTime)
    fieldWarnings.startTime = t('announcement-start-time-required');
  if (touched.endDate && !endDate)
    fieldWarnings.endDate = t('announcement-end-date-required');
  else if (endBeforeStart)
    fieldWarnings.endDate = t('announcement-end-before-start');
  if (touched.endTime && !endTime)
    fieldWarnings.endTime = t('announcement-end-time-required');
  else if (endTimeBeforeStart)
    fieldWarnings.endTime = t('announcement-end-time-before-start');
  if (touched.teamspace && !isGlobal && !selectedTeamspaceId)
    fieldWarnings.teamspace = t('announcement-teamspace-required');

  const resetForm = () => {
    setTitle('');
    setContent('');
    setType('general');
    setStartDate(new Date());
    setStartTime(currentTimeHHMM());
    setEndDate(undefined);
    setEndTime('');
    setOpenStartDate(false);
    setOpenEndDate(false);
    setIsGlobal(true);
    setSelectedTeamspaceId(currentTeamspaceId ?? '');
    setTouched({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 top-12 z-40 bg-black/20"
        onClick={handleClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') handleClose();
        }}
        role="button"
        tabIndex={0}
        data-testid="create-announcement-backdrop"
      />

      {/* Sidebar Panel */}
      <div
        data-testid="create-announcement-panel"
        className="fixed bottom-0 right-0 top-12 z-50 flex w-[440px] flex-col border-l border-[#d3d9de] bg-white shadow-xl dark:border-gray-700 dark:bg-[#262626]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-[#f2f4f5] p-4 dark:border-gray-700 dark:bg-[#393939]">
          <h3
            className="text-base font-semibold text-gray-700 dark:text-gray-300"
            data-testid="create-announcement-title"
          >
            {t('create-new-announcement')}
          </h3>
          <button
            onClick={handleClose}
            className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
            data-testid="close-create-announcement-panel"
            title={t('close')}
          >
            <Close size={16} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div
          className="flex-1 space-y-4 overflow-auto px-6 py-4"
          data-testid="create-announcement-form"
        >
          {/* Title */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              {t('announcement-title')}
              <span className="ml-0.5 text-red-500">*</span>
            </label>
            <input
              data-testid="announcement-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => markTouched('title')}
              maxLength={TITLE_MAX_LENGTH}
              placeholder={t('announcement-title-placeholder')}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-[#1f1f1f] dark:text-white"
            />
            {fieldWarnings.title && (
              <p
                className="mt-1 text-xs text-red-500"
                data-testid="warning-title"
              >
                {fieldWarnings.title}
              </p>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              {t('announcement-content')}
              <span className="ml-0.5 text-red-500">*</span>
            </label>
            <textarea
              data-testid="announcement-content-input"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onBlur={() => markTouched('content')}
              placeholder={t('announcement-content-placeholder')}
              rows={4}
              className={`w-full rounded border bg-white px-3 py-2 text-sm text-gray-900 dark:bg-[#1f1f1f] dark:text-white ${
                isContentOverLimit
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            <div className="mt-1 flex items-center justify-between">
              <div>
                {fieldWarnings.content && (
                  <p
                    className="text-xs text-red-500"
                    data-testid="warning-content"
                  >
                    {fieldWarnings.content}
                  </p>
                )}
              </div>
              <span
                className={`text-xs ${
                  isContentOverLimit
                    ? 'font-medium text-red-500'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
                data-testid="content-char-counter"
              >
                {content.length}/{CONTENT_MAX_LENGTH}
              </span>
            </div>
          </div>

          {/* Type */}
          <div>
            <label
              htmlFor="announcement-type-select"
              className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Type
            </label>
            <select
              id="announcement-type-select"
              value={type}
              onChange={(e) =>
                setType(e.target.value as 'general' | 'maintenance' | 'update')
              }
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-[#1f1f1f] dark:text-white"
            >
              <option value="general">General</option>
              <option value="maintenance">Maintenance</option>
              <option value="update">Update</option>
            </select>
          </div>

          {/* Start Date and Time */}
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('announcement-start-date')}
                <span className="ml-0.5 text-red-500">*</span>
              </Label>
              <input
                type="hidden"
                data-testid="announcement-start-date-input"
                value={startDateStr}
                onChange={(e) => {
                  const val = e.target.value;
                  setStartDate(val ? new Date(val + 'T00:00:00') : undefined);
                }}
              />
              <Popover open={openStartDate} onOpenChange={setOpenStartDate}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between text-sm font-normal"
                    data-testid="announcement-start-date-trigger"
                  >
                    {startDate
                      ? formatDateForDisplay(startDate)
                      : t('announcement-start-date')}
                    <ChevronDownIcon className="size-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto overflow-hidden p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setOpenStartDate(false);
                      markTouched('startDate');
                    }}
                    disabled={{ before: new Date() }}
                  />
                </PopoverContent>
              </Popover>
              {fieldWarnings.startDate && (
                <p
                  className="mt-1 text-xs text-red-500"
                  data-testid="warning-start-date"
                >
                  {fieldWarnings.startDate}
                </p>
              )}
            </div>
            <div>
              <Label
                htmlFor="announcement-start-time-input"
                className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                {t('announcement-start-time')}
                <span className="ml-0.5 text-red-500">*</span>
              </Label>
              <Input
                id="announcement-start-time-input"
                data-testid="announcement-start-time-input"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                onBlur={() => markTouched('startTime')}
                autoComplete="off"
                className="appearance-none bg-background [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              />
              {fieldWarnings.startTime && (
                <p
                  className="mt-1 text-xs text-red-500"
                  data-testid="warning-start-time"
                >
                  {fieldWarnings.startTime}
                </p>
              )}
            </div>
          </div>

          {/* End Date and Time */}
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('announcement-end-date')}
                <span className="ml-0.5 text-red-500">*</span>
              </Label>
              <input
                type="hidden"
                data-testid="announcement-end-date-input"
                value={endDateStr}
                onChange={(e) => {
                  const val = e.target.value;
                  setEndDate(val ? new Date(val + 'T00:00:00') : undefined);
                }}
              />
              <Popover open={openEndDate} onOpenChange={setOpenEndDate}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between text-sm font-normal"
                    data-testid="announcement-end-date-trigger"
                  >
                    {endDate
                      ? formatDateForDisplay(endDate)
                      : t('announcement-end-date')}
                    <ChevronDownIcon className="size-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto overflow-hidden p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setOpenEndDate(false);
                      markTouched('endDate');
                    }}
                    disabled={startDate ? { before: startDate } : undefined}
                  />
                </PopoverContent>
              </Popover>
              {fieldWarnings.endDate && (
                <p
                  className="mt-1 text-xs text-red-500"
                  data-testid="warning-end-date"
                >
                  {fieldWarnings.endDate}
                </p>
              )}
            </div>
            <div>
              <Label
                htmlFor="announcement-end-time-input"
                className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                {t('announcement-end-time')}
                <span className="ml-0.5 text-red-500">*</span>
              </Label>
              <Input
                id="announcement-end-time-input"
                data-testid="announcement-end-time-input"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                onBlur={() => markTouched('endTime')}
                autoComplete="off"
                className="appearance-none bg-background [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              />
              {fieldWarnings.endTime && (
                <p
                  className="mt-1 text-xs text-red-500"
                  data-testid="warning-end-time"
                >
                  {fieldWarnings.endTime}
                </p>
              )}
            </div>
          </div>

          {/* Global Announcement Checkbox */}
          <div className="flex items-center gap-2">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                data-testid="announcement-global-checkbox"
                type="checkbox"
                checked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
                className="size-4 rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t('announcement-global')}
              </span>
            </label>
          </div>

          {/* Teamspace Selector (shown when not global) */}
          {!isGlobal && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('announcement-teamspace-select')}
                <span className="ml-0.5 text-red-500">*</span>
              </label>
              <select
                data-testid="announcement-teamspace-select"
                value={selectedTeamspaceId}
                onChange={(e) => {
                  setSelectedTeamspaceId(e.target.value);
                  markTouched('teamspace');
                }}
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-[#1f1f1f] dark:text-white"
              >
                <option value="">
                  {t('announcement-teamspace-select-placeholder')}
                </option>
                {filteredTeamspaces.map((ts) => (
                  <option key={ts.id} value={ts.id}>
                    {ts.name}
                  </option>
                ))}
              </select>
              {fieldWarnings.teamspace && (
                <p
                  className="mt-1 text-xs text-red-500"
                  data-testid="warning-teamspace"
                >
                  {fieldWarnings.teamspace}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex gap-3 border-t border-gray-200 bg-[#f2f4f5] px-6 py-4 dark:border-gray-700 dark:bg-[#393939]"
          data-testid="create-announcement-footer"
        >
          <button
            onClick={handleClose}
            className="flex-1 rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            data-testid="cancel-create-announcement"
          >
            {t('cancel')}
          </button>
          <div className="relative flex-1" title={disabledTooltip}>
            <button
              onClick={handleSubmit}
              disabled={!isFormValid}
              className={`w-full rounded-lg px-4 py-2 text-sm font-medium transition-opacity ${
                isFormValid
                  ? 'bg-[#0F62FE] text-white hover:opacity-90 dark:bg-[#78A9FF] dark:text-[#262626]'
                  : 'cursor-not-allowed bg-[#0F62FE]/40 text-white/60'
              }`}
              data-testid="submit-create-announcement"
            >
              {t('Create')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateAnnouncementPanel;
